import {
  LeasingCarDto,
  postCarsSearchSearchLeasing,
  postCarsSearchSearchSale,
  SaleCarDto,
  SearchCarsRequest,
} from "api"
import {
  CarTypeOrder,
  CarTypeToClassificationCodes,
  fetchTermModelRefs,
  searchAcrossClassifications,
} from "@eurodrive/utils"
import { CarType, SearchFilters } from "./Filters/types"

export type CarDto = LeasingCarDto | SaleCarDto

export interface SearchCarsResult {
  items: CarDto[]
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  type: "leasing" | "sale"
}

const emptyResult = (
  dealType: SearchFilters["dealType"],
): SearchCarsResult => ({
  items: [],
  totalCount: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  type: dealType === "leasing" ? ("leasing" as const) : ("sale" as const),
})

/**
 * Performs a car search with the given filters and pagination
 * This function can be used both on the server and client
 */
export async function searchCars(
  filters: SearchFilters,
  currentPage: number,
): Promise<SearchCarsResult> {
  // The metadata dropdown exposes each brand's `masterManufacturerCode` as the value;
  // the search endpoint now filters by `masterManufacturers` directly (no modelCode
  // bridge / post-filter needed), so the dropdown selection and the result set agree
  // and the server's totalCount is accurate.
  const selectedMaster = filters.selectedManufacturer || null

  const models = filters.selectedModel ? [parseInt(filters.selectedModel)] : []

  const tags = filters.selectedFeatures.map((id) => parseInt(id))
  const types = filters.selectedCarTypes.map((id) => parseInt(id))
  const vehicleSingleValuedClassificationCodes =
    CarTypeToClassificationCodes[filters.carType] || []

  const searchQuery = filters.searchQuery.trim()

  // Build the request object, completely omitting empty arrays
  const searchParams: any = {
    maximumPriceRange: filters.priceRange[1],
    minimumPriceRange: filters.priceRange[0],
    requestPage: 0, // Always fetch from page 0 with increased page size
    pageSize: (currentPage + 1) * 15, // Increase page size to simulate pagination
  }

  // Only add array fields if they have values.
  if (selectedMaster)
    searchParams.masterManufacturers = [parseInt(selectedMaster)]
  if (models.length > 0) searchParams.models = models
  if (tags.length > 0) searchParams.tags = tags
  if (types.length > 0) searchParams.types = types
  if (vehicleSingleValuedClassificationCodes.length > 0)
    searchParams.vehicleSingleValuedClassificationCodes =
      vehicleSingleValuedClassificationCodes

  // Add saleCarClassificationType for new/used filtering
  if (filters.dealType === "new") {
    searchParams.saleCarClassificationType = "NewCar"
  } else if (filters.dealType === "used") {
    searchParams.saleCarClassificationType = "UsedCar"
  }

  // Add year and KM filtering for used cars
  if (filters.dealType === "used") {
    if (filters.yearFrom) {
      searchParams.minimumYear = parseInt(filters.yearFrom)
    }
    if (filters.yearTo) {
      searchParams.maximumYear = parseInt(filters.yearTo)
    }
    if (filters.kmFrom) {
      searchParams.minimumKm = parseInt(filters.kmFrom)
    }
    if (filters.kmTo) {
      searchParams.maximumKm = parseInt(filters.kmTo)
    }
  }

  // A free-text term is resolved by the CMS to raw (manufacturer, model) pairs,
  // which the API expands to whole master-model groups. The filtering happens
  // server-side, so the count and "load more" come straight from the response.
  if (searchQuery) {
    try {
      const refs = await fetchTermModelRefs(searchQuery)
      const rawModels =
        filters.dealType === "leasing" ? refs.leasing : refs.sale

      // An unmatched term yields no cars instead of the whole category.
      if (rawModels.length === 0) return emptyResult(filters.dealType)

      searchParams.rawModels = rawModels
    } catch (error) {
      console.warn(
        "[searchCars] text search term could not be resolved, returning the category unfiltered",
        error,
      )
    }
  }

  // Categories that span more than one classification code are fetched per
  // code and merged, because the API does not union them (see
  // searchAcrossClassifications)
  try {
    const carResponse =
      filters.dealType === "leasing"
        ? await searchAcrossClassifications<LeasingCarDto>(
            searchParams,
            (request) => postCarsSearchSearchLeasing(request, "he"),
          )
        : await searchAcrossClassifications<SaleCarDto>(
            searchParams,
            (request) => postCarsSearchSearchSale(request, "he"),
          )

    return {
      items: carResponse.items,
      totalCount: carResponse.totalCount,
      hasNextPage: carResponse.hasNextPage,
      hasPreviousPage: carResponse.hasPreviousPage,
      type:
        filters.dealType === "leasing"
          ? ("leasing" as const)
          : ("sale" as const),
    }
  } catch (error) {
    console.error("[searchCars] car search failed", error)
    return emptyResult(filters.dealType)
  }
}

/**
 * Fetches car counts for each search category (Premium, Standard) for leasing.
 * Used to determine which category has inventory for smart defaulting.
 */
export async function getLeasingCarTypeCounts(): Promise<
  Record<CarType, number>
> {
  const baseParams: Omit<
    SearchCarsRequest,
    "vehicleSingleValuedClassificationCodes"
  > = {
    minimumPriceRange: 0,
    maximumPriceRange: 9999999,
    maximumKm: 0,
    minimumKm: 0,
    maximumYear: 0,
    minimumYear: 0,
    manufacturers: [],
    masterManufacturers: [],
    models: [],
    rawModels: [],
    tags: [],
    types: [],
    hand: "None",
    saleCarClassificationType: null,
    vehicleNumbers: [],
    requestPage: 0,
    pageSize: 1,
  }

  const countFor = (codes: number[]) =>
    searchAcrossClassifications<LeasingCarDto>(
      { ...baseParams, vehicleSingleValuedClassificationCodes: codes },
      (request) => postCarsSearchSearchLeasing(request, "he"),
    )

  try {
    const [premiumResult, standardResult] = await Promise.all([
      countFor(CarTypeToClassificationCodes.Premium),
      countFor(CarTypeToClassificationCodes.Standard),
    ])

    return {
      Premium: premiumResult.totalCount ?? 0,
      Standard: standardResult.totalCount ?? 0,
    }
  } catch (error) {
    console.error("Error fetching leasing car type counts:", error)
    return {
      Premium: 0,
      Standard: 0,
    }
  }
}

/**
 * Determines the best carType to default to based on inventory counts
 * Priority order: Premium -> Standard
 */
export function getDefaultCarTypeWithInventory(
  counts: Record<CarType, number>,
): CarType {
  return CarTypeOrder.find((type) => counts[type] > 0) || "Premium"
}
